import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Trash2, ShoppingBasket, Loader2, TicketPercent } from "lucide-react";
import { api, mediaSrc, money, formatApiError } from "@/lib/api";
import { removeFromCart } from "@/lib/cart";

export default function CartDrawer({ open, onClose, cart }) {
  const [checkingOut, setCheckingOut] = useState(false);
  const [creditInput, setCreditInput] = useState("");
  const [credit, setCredit] = useState(null);
  const [checkingCredit, setCheckingCredit] = useState(false);
  const total = cart.reduce((s, i) => s + (parseFloat(i.price) || 0), 0);
  const creditApplied = credit ? Math.min(credit.remaining, total) : 0;

  const applyCredit = async () => {
    if (!creditInput.trim()) return;
    setCheckingCredit(true);
    try {
      const { data } = await api.get("/credit/check", { params: { code: creditInput.trim() } });
      if (data.remaining <= 0) {
        toast.error("That credit code has been fully used");
        return;
      }
      setCredit(data);
      toast.success(`Credit applied — ${money(Math.min(data.remaining, total))} off this order`);
    } catch (e) {
      toast.error(formatApiError(e, "That credit code was not recognised"));
    } finally {
      setCheckingCredit(false);
    }
  };

  const checkout = async () => {
    setCheckingOut(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        item_ids: cart.map((i) => i.id),
        origin_url: window.location.origin,
        credit_code: credit ? credit.code : undefined,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e, "Checkout failed. Please try again."));
      setCheckingOut(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[80] bg-[#0A0A0A]/60 backdrop-blur-sm"
            data-testid="cart-backdrop"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[90] w-full max-w-md bg-[#F3EDE3] border-l border-[#DCD3C3] flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#DCD3C3]">
              <div className="flex items-center gap-3">
                <ShoppingBasket className="w-5 h-5 text-[#B38728]" />
                <h2 className="font-serif text-2xl">Your Basket</h2>
              </div>
              <button onClick={onClose} data-testid="cart-close-button" aria-label="Close basket" className="text-[#5C564C] hover:text-[#0A0A0A] transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              {cart.length === 0 && (
                <p className="text-sm text-[#5C564C] py-16 text-center" data-testid="cart-empty">
                  Your basket is empty — the vault awaits.
                </p>
              )}
              {cart.map((item) => (
                <div key={item.id} className="flex items-center gap-4 bg-white border border-[#DCD3C3] p-3" data-testid={`cart-item-${item.id}`}>
                  {item.image_url ? (
                    <img src={mediaSrc(item.image_url)} alt="" className="w-16 h-16 object-cover border border-[#DCD3C3] shrink-0" />
                  ) : (
                    <div className="w-16 h-16 bg-[#EAE2D5] border border-[#DCD3C3] shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-sm leading-snug line-clamp-2">{item.title}</p>
                    <p className="font-mono text-[11px] text-[#B38728] mt-1 tracking-widest">{money(item.price)}</p>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.id)}
                    data-testid={`cart-remove-${item.id}`}
                    aria-label="Remove from basket"
                    className="p-2 border border-[#DCD3C3] text-[#5C564C] hover:border-red-700 hover:text-red-700 transition-colors shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {cart.length > 0 && (
              <div className="border-t border-[#DCD3C3] px-6 py-5">
                <div className="mb-4" data-testid="cart-credit-section">
                  <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C] flex items-center gap-2">
                    <TicketPercent className="w-3.5 h-3.5 text-[#B38728]" /> Store credit code
                  </label>
                  {credit ? (
                    <div className="mt-2 flex items-center justify-between border border-[#B38728] bg-[#FDF9EF] px-3 py-2.5" data-testid="cart-credit-applied">
                      <p className="font-mono text-xs tracking-widest text-[#0A0A0A]">
                        {credit.code} <span className="text-[#B38728]">−{money(creditApplied)}</span>
                      </p>
                      <button onClick={() => { setCredit(null); setCreditInput(""); }} data-testid="cart-credit-remove" className="text-[#5C564C] hover:text-red-700 transition-colors" aria-label="Remove credit code">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="mt-2 flex gap-2">
                      <input
                        value={creditInput}
                        onChange={(e) => setCreditInput(e.target.value.toUpperCase())}
                        placeholder="SX-…"
                        data-testid="cart-credit-input"
                        className="flex-1 bg-white border border-[#DCD3C3] px-3 py-2.5 text-sm font-mono tracking-widest placeholder-[#a79f90] focus:outline-none focus:border-[#B38728] transition-colors"
                      />
                      <button
                        onClick={applyCredit}
                        disabled={checkingCredit || !creditInput.trim()}
                        data-testid="cart-credit-apply"
                        className="border border-[#0A0A0A] px-4 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#0A0A0A] hover:text-[#F3EDE3] transition-colors disabled:opacity-50"
                      >
                        {checkingCredit ? "…" : "Apply"}
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between mb-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C]">Total</p>
                  <div className="text-right">
                    {creditApplied > 0 && <p className="font-mono text-xs text-[#B38728] line-through" data-testid="cart-total-before-credit">{money(total)}</p>}
                    <p className="font-serif text-3xl" data-testid="cart-total">{money(Math.max(total - creditApplied, 0))}</p>
                  </div>
                </div>
                <button
                  onClick={checkout}
                  disabled={checkingOut}
                  data-testid="cart-checkout-button"
                  className="w-full bg-[#0A0A0A] text-[#F3EDE3] py-4 font-mono text-xs uppercase tracking-[0.3em] hover:bg-[#B38728] hover:text-[#0A0A0A] transition-colors disabled:opacity-50 flex items-center justify-center gap-3"
                >
                  {checkingOut && <Loader2 className="w-4 h-4 animate-spin" />}
                  {checkingOut ? "Opening secure checkout…" : "Checkout Securely"}
                </button>
                <p className="text-[10px] text-[#5C564C] text-center mt-3 font-mono uppercase tracking-[0.2em]">
                  Tracked &amp; insured shipping confirmed after purchase
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
